import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import {
  loginWithEmailPassword,
  loginWithGoogle,
} from '@/lib/api/auth'
import { toast } from 'sonner'

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z" fill="#4285F4"/>
    <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
    <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54772 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
    <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
  </svg>
)

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectionUrl = searchParams.get('redirection_url')
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  
  const handleEmailPasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !password) {
      setError('Please enter both email and password')
      return
    }
    
    setIsLoading(true)
    setError(null)
    
    try {
      const result = await loginWithEmailPassword({ email, password })
      if (!result.otp_sent) {
        throw new Error('Invalid login, failed to send OTP. Please try again.')
      }
      const platform = searchParams.get('platform') ?? undefined
      navigate('/verify-otp', { state: { email, redirectionUrl, platform } })
    } catch (error: any) {
      console.error('Login error:', error)
      toast.error(error.message || 'Login failed. Please try again.')
      setError(error.message || 'Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true)
    setError(null)

    try {
      // Persist flow context across the OAuth redirect (React Router state is lost)
      if (redirectionUrl) sessionStorage.setItem('sso_redirection_url', redirectionUrl)
      else sessionStorage.removeItem('sso_redirection_url')

      const platform = searchParams.get('platform')
      if (platform) sessionStorage.setItem('sso_platform', platform)
      else sessionStorage.removeItem('sso_platform')

      const response = await loginWithGoogle()
      // Redirect to Google OAuth URL
      window.location.href = response.data.url
    } catch (error: any) {
      console.error('Google login error:', error)
      toast.error(error.message || 'Google login failed. Please try again.')
      setError(error.message || 'Google login failed. Please try again.')
      setIsGoogleLoading(false)
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <div className="text-center mb-2">
        <h2 className="text-2xl font-bold text-white">Sign In to Your</h2>
        <h2 className="text-2xl font-bold text-white">Hoomi Account</h2>
      </div>
      
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-white p-3 rounded-md text-sm">
          {error}
        </div>
      )}
      
      <form onSubmit={handleEmailPasswordLogin} className="flex flex-col gap-4">
        <Input
          type="email"
          placeholder="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-white/90 border-0 text-gray-900 placeholder:text-gray-500 h-12 rounded-lg text-white"
          disabled={isLoading}
        />
        
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-white/90 border-0 text-gray-900 placeholder:text-gray-500 h-12 rounded-lg text-white"
          disabled={isLoading}
        />
        
        <button
          type="button"
          className="text-white text-sm text-center underline hover:text-purple-300 transition-colors"
          onClick={() => navigate('/forgot-password')}
        >
          I forgot my password
        </button>
        
        <Button 
          type="submit"
          className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white h-12 rounded-lg font-semibold"
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
              Signing In...
            </div>
          ) : (
            'Sign In'
          )}
        </Button>
        
        <div className="flex items-center gap-4 my-2">
          <Button 
            type="button"
            variant="outline" 
            className="flex-1 bg-white/90 border-0 text-gray-900 h-12 rounded-lg hover:bg-white transition-all" 
            onClick={handleGoogleLogin}
            disabled={isGoogleLoading || isLoading}
          >
            {isGoogleLoading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-900 border-t-transparent"></div>
            ) : (
              <>
                <GoogleIcon />
                <span className="ml-2 text-white">Login with Google</span>
              </>
            )}
          </Button>
        </div>
        
        <button
          type="button"
          className="text-white text-sm text-center underline hover:text-purple-300 transition-colors"
          onClick={() => navigate('/register')}
        >
          I don't have an Account
        </button>
      </form>
      
      <div className="text-center text-xs text-white/50 mt-2">
        <Link to="https://hoomi.social/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 underline transition-colors">
          Privacy Policy
        </Link>
        {' | '}
        <Link to="https://hoomi.social/terms" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 underline transition-colors">
          Terms &amp; Conditions
        </Link>
      </div>
    </div>
  )
}
